import { IconCheck } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import useIndexRatePlan from "../../../services/rate-plan/hooks/useIndexRatePlan";
import Select, { StylesConfig, components, MultiValueProps } from "react-select";

interface OptionType {
    value: string;
    label: string;
}

interface RatePlanFilterProps {
    setRatePlanFilter: (ratePlanFilter: string[]) => void;
    menuZIndex?: number;
    ratePlanQuery?: any;
}

const RatePlanFilter: React.FC<RatePlanFilterProps> = ({ setRatePlanFilter, menuZIndex, ratePlanQuery }) => {
    const defaultRatePlan = { value: "0", label: "All Rates" };
    const [ratePlan, setRatePlan] = useState<OptionType[]>([defaultRatePlan]);
    const [selectedRatePlans, setSelectedRatePlans] = useState<OptionType[]>([defaultRatePlan]);

    const fallbackQuery = useIndexRatePlan({ include: "roomType", all: true });
    const activeQuery = ratePlanQuery || fallbackQuery;
    const { data: ratePlanData, isSuccess, isError, isFetching } = activeQuery;

    useEffect(() => {
        if (ratePlanData) {
            setRatePlan([
                defaultRatePlan,
                ...ratePlanData.data.map((ratePlan: any) => ({
                    value: ratePlan.id,
                    label: `${ratePlan.room_type ? `${ratePlan.room_type.name} -` : ''} ${ratePlan.name}`,
                })),
            ]);
            console.log('ratePlanData', ratePlanData);
        }
    }, [ratePlanData]);

    useEffect(() => {
        if (ratePlanData && selectedRatePlans.length === 1 && selectedRatePlans[0].value === defaultRatePlan.value) {
            setRatePlanFilter(
                ratePlanData.data
                    .filter((rate: any) => rate.id !== defaultRatePlan.value)
                    .map((rate: any) => rate.id)
            );
        }
    }, [ratePlanData, selectedRatePlans]);

    return (
        <RatePlanSelect
            defaultRatePlan={defaultRatePlan}
            ratePlan={ratePlan}
            isFetching={isFetching}
            isSuccess={isSuccess}
            isError={isError}
            selectedRatePlans={selectedRatePlans}
            setSelectedRatePlans={setSelectedRatePlans}
            setRatePlanFilter={setRatePlanFilter}
            menuZIndex={menuZIndex}
        />
    );
};

export default RatePlanFilter;

interface RatePlanSelectProps {
    defaultRatePlan: OptionType;
    ratePlan: OptionType[];
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    selectedRatePlans: OptionType[];
    setSelectedRatePlans: (ratePlans: OptionType[]) => void;
    setRatePlanFilter?: (ratePlanFilter: string[]) => void;
    menuZIndex?: number;
}

const CustomOption = (props: any) => {
    const { data, isSelected, innerRef, innerProps } = props;

    return (
        <div ref={innerRef} {...innerProps} className="d-flex justify-content-between align-items-center p-2">
            <span>{data.label}</span>
            {isSelected && <IconCheck size={16} />}
        </div>
    );
};

const RatePlanSelect: React.FC<RatePlanSelectProps> = ({
    defaultRatePlan,
    ratePlan,
    isFetching,
    isSuccess,
    isError,
    selectedRatePlans,
    setSelectedRatePlans,
    setRatePlanFilter,
    menuZIndex = 1000,
}) => {
    const customStyles: StylesConfig<OptionType, boolean> = {
        menuPortal: (base) => ({
            ...base,
            zIndex: menuZIndex, // Pastikan z-index cukup tinggi
        }),
        control: (provided) => ({
            ...provided,
            minWidth: "200px", // Atur minimum width
        }),
        menu: (provided) => ({
            ...provided,
            minWidth: "200px", // Jika ingin menu dropdown mengikuti min-width
        }),
    };

    const handleChange = (selected: OptionType[]) => {
        if (selected.length === 1 && selected[0].value === defaultRatePlan.value) {
            setSelectedRatePlans([defaultRatePlan]);
            setRatePlanFilter?.(ratePlan.filter((rate) => rate.value !== defaultRatePlan.value).map((rate) => rate.value));
        } else {
            setSelectedRatePlans(selected.filter((ratePlan) => ratePlan.value !== defaultRatePlan.value));
            setRatePlanFilter?.(selected.filter((ratePlan) => ratePlan.value !== defaultRatePlan.value).map((ratePlan) => ratePlan.value));
        }
    };

    return (
        <Select
            value={selectedRatePlans}
            options={ratePlan}
            className="p-0"
            styles={customStyles}
            menuPortalTarget={document.body}
            menuPosition="absolute"
            isLoading={isFetching}
            isDisabled={!isSuccess || isError}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={(selected) => handleChange(selected as OptionType[])}
            components={{
                Option: CustomOption,
                MultiValueContainer: ({ children, ...props }) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const totalSelected = selectedValues.length;
                    const maxVisible = 3; // Maksimal item yang ditampilkan sebelum "X More"

                    if (index < maxVisible) {
                        return children;
                    }

                    if (index === maxVisible) {
                        const remainingCount = totalSelected - maxVisible;
                        return (
                            <div className="d-flex align-items-center px-2 bg-light rounded">
                                <span>+{remainingCount} More</span>
                            </div>
                        );
                    }

                    return null;
                },
                MultiValue: (props: MultiValueProps<any>) => {
                    const selectedValues = props.selectProps.value as OptionType[];
                    const index = selectedValues.findIndex((v) => v.value === props.data.value);
                    const maxVisible = 3;

                    if (index > maxVisible) return null;

                    return <components.MultiValue {...props} />;
                },
                ValueContainer: ({ getValue }) => {
                    const selected = getValue();
                    return (
                        <div className="p-2">
                            {selected.length === 1 && selected[0].value === defaultRatePlan.value
                                ? defaultRatePlan.label
                                : `${selected.length} Rate Plan Selected`}
                        </div>
                    );
                },
            }}
        />
    );
};
